import { FC } from 'react';
import { Box, HStack, Text } from '@chakra-ui/react';

import {
  CurrentValueIndicator,
  EvolutionArrow,
} from 'features/Dashboard/pages/components/CurrentValueIndicator';
import { useArrowDirection } from 'features/Dashboard/pages/components/CurrentValueIndicator/hooks/useArrowDirection';
import { DeltaIndicator } from 'features/Dashboard/pages/components/DeltaIndicator';
import { useGraphColorSchema } from 'features/Dashboard/pages/hooks/useGraphColorSchema';

import dashboardLocales from '../../../../../../../common/locales';

interface InlineDeltaIndicatorProps {
  delta?: number | null;
  currentValue?: number | null;
  displayDelta?: boolean;
  colorSchema?: 'arrow-normal' | 'arrow-reversed';
}

interface TransactionCountProps {
  transactionCount?: number | null;
  currentValue?: number | null;
}

const TransactionCountIndicator: FC<TransactionCountProps> = ({
  currentValue,
  transactionCount,
}) => {
  return (
    <Box>
      <CurrentValueIndicator
        arrowColors={{
          arrow: '',
          background: '',
          border: '',
        }}
        currentValue={currentValue ?? 0}
        delta={null}
        displayArrow={false}
        titleFontSize="font-15"
      />

      <Text>
        {dashboardLocales.formatString(
          transactionCount === 1
            ? dashboardLocales.transactionCount.singular
            : dashboardLocales.transactionCount.plural,
          {
            count: transactionCount || 0,
          },
        )}
      </Text>
    </Box>
  );
};

const InlineDeltaIndicator: FC<InlineDeltaIndicatorProps> = ({
  colorSchema = 'arrow-normal',
  currentValue,
  delta,
  displayDelta = true,
}) => {
  const colors = useGraphColorSchema({
    delta: delta ?? null,
    evolutionColorSchema: colorSchema,
  });

  const direction = useArrowDirection(delta ?? null);

  return (
    <HStack spacing="space-16">
      {displayDelta ? (
        <EvolutionArrow
          backgroundColor={colors.arrowBg}
          borderColor={colors.arrowBorder}
          color={colors.arrow}
          direction={direction}
          size="sm"
        />
      ) : null}
      <Box>
        <CurrentValueIndicator
          arrowColors={{
            arrow: '',
            background: '',
            border: '',
          }}
          currentValue={currentValue ?? 0}
          delta={delta ?? null}
          displayArrow={false}
          titleFontSize="font-15"
        />

        {displayDelta ? (
          <DeltaIndicator color={colors.arrow} delta={delta ?? null} />
        ) : null}
      </Box>
    </HStack>
  );
};

export type StatIndicatorMode = 'transaction-count' | 'delta' | 'simple';

export const StatIndicator: FC<{
  transactionCount?: number | null;
  currentValue?: number | null;
  delta?: number | null;
  mode: StatIndicatorMode;
  colorSchema?: 'arrow-normal' | 'arrow-reversed';
}> = ({ colorSchema, currentValue, delta, mode, transactionCount }) => {
  if (mode === 'transaction-count') {
    return (
      <TransactionCountIndicator
        currentValue={currentValue}
        transactionCount={transactionCount}
      />
    );
  }

  if (mode === 'simple') {
    return (
      <InlineDeltaIndicator
        currentValue={currentValue}
        delta={delta}
        displayDelta={false}
      />
    );
  }

  return (
    <InlineDeltaIndicator
      colorSchema={colorSchema}
      currentValue={currentValue}
      delta={delta}
    />
  );
};
